import { Link } from '@tanstack/react-router';
import { Trans, useLingui } from '@lingui/react/macro';
import { PrivacySettingsLink } from '../../-components/privacy-settings-link';
import { ArrowUpRight, Instagram } from 'lucide-react';
import { WorldcooLogo } from '@worldcoo/ui/custom/worldcoo-logo';
import { Image } from '@/routes/-components/image';
import { DARK_FORM_FIELDS, Squiggle } from './primitives';
import { lazy, Suspense } from 'react';
import { NewsletterForm } from './newsletter';
import { LottiePlayer } from './lottie/lottie-player';
import footerAnimation from './lottie/footer.json';

// Lazy, like `www` does it: the form pulls in zod, the form runtime and the terms dialog, none of which
// most visitors ever need — and on the pages without the form variant it is never requested at all.
const ContactForm = lazy(() => import('../../-components/contact-form').then((m) => ({ default: m.ContactForm })));

/** Solid LinkedIn glyph (lucide only ships the outline one) — filled badge in `currentColor`. */
function LinkedinFilled(props: { className?: string }) {
	return (
		<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
			<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 1 1 0-4.124 2.062 2.062 0 0 1 0 4.124zM7.119 20.452H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
		</svg>
	);
}

/** Internal routes that exist today (this Epic's stubs). Others link out or are placeholders. */
type InternalRoute =
	| '/new'
	| '/new/solutions'
	| '/new/partner-ngos'
	| '/new/about-us'
	| '/new/contact'
	| '/www/legal-disclaimer'
	| '/www/privacy-policy'
	| '/www/cookies';
type FooterLink = { label: string; to?: InternalRoute; href?: string };

function FooterLink({ link }: { link: FooterLink }) {
	const className = 'hover:text-brand-strong transition-colors';
	if (link.to) {
		return (
			<Link to={link.to} className={className}>
				{link.label}
			</Link>
		);
	}
	return (
		<a href={link.href ?? '#'} className={className}>
			{link.label}
		</a>
	);
}

/**
 * The site footer. Two heads on one body: the default sends people off to the two entry points, while
 * `cta="form"` swaps that for the contact form the hand-off puts on the Solutions page. The green card
 * below is identical in both.
 */
export function Footer(props: { cta?: 'links' | 'form' }) {
	const { t } = useLingui();
	const currentYear = new Date().getFullYear();

	const columns: { title: string; links: FooterLink[]; social?: boolean; bcorp?: boolean }[] = [
		{
			title: t`Company`,
			bcorp: true,
			links: [
				{ label: t`About us`, to: '/new/about-us' },
				{ label: t`Brandbook`, href: '#' },
				{ label: t`Join our team`, href: '#' },
				{ label: t`FAQ`, href: '#' },
				{ label: t`Contact`, to: '/new/contact' },
			],
		},
		{
			title: t`Solutions`,
			social: true,
			links: [
				{ label: t`Charity rounding up`, to: '/new/solutions' },
				{ label: t`Solidarity euro`, href: '#' },
				{ label: t`Solidarity portals`, href: '#' },
				{ label: t`Payroll rounding`, href: '#' },
				{ label: t`Tech partners`, href: '#' },
			],
		},
		{
			title: t`NGO`,
			links: [
				{ label: t`Partner NGOs`, to: '/new/partner-ngos' },
				{ label: t`Selection process`, href: '#' },
				{ label: t`NGO sign-up`, href: '#' },
				{ label: t`WebApp`, href: 'https://app.worldcoo.com/' },
				{ label: t`Certificates`, href: 'https://app.worldcoo.com/' },
			],
		},
	];

	const heading = (
		<h2 className="text-4xl font-semibold md:text-5xl">
			<Trans>
				Ready to make real <Squiggle lineClassName="text-olive">impact</Squiggle>?
			</Trans>
		</h2>
	);
	const blurb = (
		<p className="text-lg text-white/70">
			{t`Join our network of companies turning every purchase into an opportunity for change. Tell us your case and we'll find the right solution for you.`}
		</p>
	);

	return (
		<footer className="text-ink">
			{props.cta === 'form' ? (
				<section className="bg-ink px-6 py-20 text-white md:py-28">
					<div className="mx-auto grid max-w-[1440px] items-start gap-12 lg:grid-cols-2 lg:gap-20 xl:px-10">
						<div className="flex flex-col gap-6">
							{heading}
							{blurb}
						</div>
						{/* The dark surface is applied from here rather than by forking the form — see
						    DARK_FORM_FIELDS. */}
						<div className={DARK_FORM_FIELDS}>
							<Suspense fallback={<div className="min-h-[520px]" />}>
								<ContactForm variant="company" />
							</Suspense>
						</div>
					</div>
				</section>
			) : (
				<section className="bg-ink px-6 py-20 text-center text-white md:py-28">
					<div className="mx-auto flex max-w-3xl flex-col items-center gap-6">
						{heading}
						{blurb}
						<div className="flex flex-wrap justify-center gap-4">
							<Link
								to="/new/contact"
								className="flex items-center gap-2 rounded-full bg-white py-2.5 pr-2.5 pl-5 font-medium text-black transition-opacity hover:opacity-90"
							>
								{t`For companies`}
								<span className="bg-ink grid size-6 place-items-center rounded-full text-white">
									<ArrowUpRight className="size-4" />
								</span>
							</Link>
							<Link
								to="/new/partner-ngos"
								className="flex items-center gap-2 rounded-full bg-white py-2.5 pr-2.5 pl-5 font-medium text-black transition-opacity hover:opacity-90"
							>
								{t`For NGOs`}
								<span className="bg-ink grid size-6 place-items-center rounded-full text-white">
									<ArrowUpRight className="size-4" />
								</span>
							</Link>
						</div>
					</div>
				</section>
			)}

			<div className="bg-ink px-4 pb-4">
				<div className="bg-olive text-olive-foreground relative mx-auto max-w-[1440px] overflow-hidden rounded-3xl px-8 py-12 md:px-14 md:py-16">
					<LottiePlayer
						animationData={footerAnimation}
						ariaLabel={t`A finger touching a heart`}
						className="pointer-events-none absolute top-10 right-6 hidden w-52 lg:block xl:right-10 xl:w-64"
					/>

					<div className="grid gap-10 lg:grid-cols-[1.4fr_1fr_1fr_1fr] lg:pr-56 xl:pr-72">
						<div className="flex flex-col gap-6">
							<Link to="/new" aria-label={t`Worldcoo home`}>
								<WorldcooLogo className="h-12 w-auto" accentClassName="text-ink" />
							</Link>
							<NewsletterForm />
						</div>

						{columns.map((col) => (
							<nav key={col.title} className="flex flex-col gap-3 text-sm">
								<p className="text-olive-foreground/60 font-semibold">{col.title}</p>
								<ul className="flex flex-col gap-2.5">
									{col.links.map((link) => (
										<li key={link.label}>
											<FooterLink link={link} />
										</li>
									))}
								</ul>
								{col.bcorp && (
									<a
										href="https://www.bcorporation.net/en-us/find-a-b-corp/company/worldcoo"
										target="_blank"
										rel="noopener noreferrer"
										className="mt-8 w-fit transition-opacity hover:opacity-80"
									>
										<Image
											src="/bcorp-logo-no-border-gray.webp"
											height={64}
											aspectRatio={96 / 161}
											alt={t`B Corp Certified`}
										/>
									</a>
								)}
								{col.social && (
									<div className="mt-8 flex flex-col gap-3">
										<p className="text-olive-foreground/60 font-semibold">{t`Follow us`}</p>
										<div className="flex gap-4">
											<a href="https://www.instagram.com/worldcoo/" target="_blank" rel="noopener noreferrer">
												<span className="sr-only">Instagram</span>
												<Instagram className="hover:text-brand-strong size-5 transition-colors" />
											</a>
											<a href="https://www.linkedin.com/company/worldcoo" target="_blank" rel="noopener noreferrer">
												<span className="sr-only">LinkedIn</span>
												<LinkedinFilled className="hover:text-brand-strong size-5 transition-colors" />
											</a>
										</div>
									</div>
								)}
							</nav>
						))}
					</div>

					<div className="border-olive-foreground/20 text-olive-foreground/70 relative mt-12 flex flex-wrap items-center justify-between gap-3 border-t pt-6 text-xs">
						<p className="flex flex-wrap items-center gap-x-3 gap-y-1">
							<span>
								&copy; {currentYear} Worldcoo. {t`All rights reserved`}
							</span>
							<Link to="/www/legal-disclaimer" className="hover:text-brand-strong transition-colors">
								{t`Terms of use`}
							</Link>
							<span aria-hidden>·</span>
							<Link to="/www/privacy-policy" className="hover:text-brand-strong transition-colors">
								{t`Privacy Policy`}
							</Link>
							<span aria-hidden>·</span>
							<Link to="/www/cookies" className="hover:text-brand-strong transition-colors">
								{t`Cookies`}
							</Link>
							<span aria-hidden>·</span>
							<PrivacySettingsLink className="hover:text-brand-strong transition-colors" />
						</p>
					</div>
				</div>
			</div>
		</footer>
	);
}
